import { CourseProps, LearningActivity, LearningActivityStatus, RegistrationStatus, WaitlistEnabled } from '../types';
import { isAfterClassStartTime, isBeforeClassEndTime, PartnerOrganizationType } from '../utils';
import { ApplicationUser } from '../../hooks/auth/useAuth';
import { convertLearningActivityToCourse } from '../courseBuilder';
import { UserRequestData } from '../../hooks/user/getCurrentUser';

export type CourseCondition = {
    isBeforeClassStartTime: boolean;
    isBeforeClassEndTime: boolean;
    isWaitlistEligible: boolean;
    hasSeatsAvailable: boolean;
    isCourseActive: boolean;
    isPartnerPublicUnlistedClass: boolean;
    orgIds: string[] | undefined;
    registrationStatus: RegistrationStatus | undefined;
    teamIds: string[] | undefined;
};

export const DEFAULT_COURSE_CONDITION: CourseCondition = {
    isBeforeClassStartTime: false,
    isBeforeClassEndTime: false,
    isWaitlistEligible: false,
    hasSeatsAvailable: false,
    isCourseActive: false,
    isPartnerPublicUnlistedClass: false,
    orgIds: undefined,
    registrationStatus: undefined,
    teamIds: undefined,
};

type CoursePropsForCondition = Pick<
    CourseProps,
    | 'waitlistEnabled'
    | 'schedule'
    | 'courseStatus'
    | 'isPartnerPublicUnlistedClass'
    | 'commercialPrivateCourseOrgIds'
    | 'registrationStatus'
    | 'teamIds'
>;

export const getCourseCondition = (course: CoursePropsForCondition): CourseCondition => {
    const isWaitlistEligible = course.waitlistEnabled !== WaitlistEnabled.DISABLED && course.schedule.seatsRemaining === 0;
    const hasSeatsAvailable = course.schedule.seatsRemaining > 0;
    const isCourseActive = course.courseStatus === LearningActivityStatus.ACTIVE;
    const isPartnerPublicUnlistedClass = course.isPartnerPublicUnlistedClass;
    const orgIds = course.commercialPrivateCourseOrgIds || [];

    return {
        isBeforeClassStartTime: !isAfterClassStartTime(course),
        isBeforeClassEndTime: isBeforeClassEndTime(course),
        isWaitlistEligible,
        hasSeatsAvailable,
        isCourseActive,
        isPartnerPublicUnlistedClass,
        orgIds,
        registrationStatus: course.registrationStatus,
        teamIds: course.teamIds,
    };
};

export const getCourseConditionFromLearningActivity = (learningActivity: LearningActivity) => {
    const course = convertLearningActivityToCourse(learningActivity, undefined);
    if (course) {
        return getCourseCondition(course);
    }
};

export type UserRequest = {
    resourceIds: string[];
    orgId: string;
    requestStatus: string;
};

export type UserCondition = {
    isUserLoaded: boolean;
    isPartnerUser: boolean;
    isCustomerB2IUser: boolean;
    isPartnersB2IUser: boolean;
    userOrganizationId: string | undefined;
    learnerTeamIds: string[] | undefined;
    requestingAccessToOrgId: string | undefined;
    userRequests: UserRequest[] | undefined;
};

export const getUserConditions = (
    user: ApplicationUser,
    requestingAccessToOrgId?: string,
    userRequests?: UserRequestData[],
): UserCondition => {
    const isUserLoaded = !!user.id && !!user.organizationId;
    const isPartnerUser =
        (user.organizationType && Object.values(PartnerOrganizationType).includes(user.organizationType as PartnerOrganizationType)) ||
        false;
    const isCustomerB2IUser = user.organizationType === 'CUSTOMERS_B2I';
    const isPartnersB2IUser = user.organizationType === 'PARTNERS_B2I';

    const mappedUserRequests = userRequests?.map((request) => ({
        resourceIds: request.resourceIds,
        orgId: request.organization.id,
        requestStatus: request.userRequestStatus,
    }));

    return {
        isUserLoaded,
        isPartnerUser,
        isCustomerB2IUser,
        isPartnersB2IUser,
        userOrganizationId: user.organizationId,
        learnerTeamIds: user.teamIds,
        requestingAccessToOrgId,
        userRequests: mappedUserRequests,
    };
};

export type FeatureFlagsCondition = {
    isTeamAuthorizationEnabled: boolean;
    hideJoinButtonForNonWebExCourse: boolean;
    privateLinkSharing: boolean;
    adcLinkSharing: boolean;
};
